/**
 * Routing and the sign-in gate.
 *
 * Every sidebar entry is a real route, so pages are linkable, the back button
 * works, and a refresh lands where you were. The four client lists share one
 * component and differ only by the preset filter their route passes in.
 */
import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { homeFor } from './constants/defaults.js';

import { AppProvider, useApp } from './store/AppContext.jsx';
import { hasSession } from './lib/api.js';
import { offlineAllowed } from './lib/storage.js';

import AppShell from './components/AppShell.jsx';
import BootScreen from './components/BootScreen.jsx';
import Login from './pages/Login.jsx';

import ClientList from './pages/ClientList.jsx';
import ClientDetail from './pages/ClientDetail.jsx';
import ClientReport from './pages/ClientReport.jsx';
import DemoBookings from './pages/DemoBookings.jsx';
import DemoReports from './pages/DemoReports.jsx';
import FollowUps from './pages/FollowUps.jsx';
import NeedsAction from './pages/NeedsAction.jsx';
import Insights from './pages/Insights.jsx';
import Billing from './pages/Billing.jsx';
import Outbox from './pages/Outbox.jsx';
import Copilot from './pages/Copilot.jsx';
import SalesOverview from './pages/SalesOverview.jsx';
import Pipeline from './pages/Pipeline.jsx';
import LeadDetail from './pages/LeadDetail.jsx';
import Deals from './pages/Deals.jsx';
import Contacts from './pages/Contacts.jsx';
import Companies from './pages/Companies.jsx';
import Activities from './pages/Activities.jsx';
import SalesReports from './pages/SalesReports.jsx';
import Settings from './pages/settings/Settings.jsx';

import Campaigns from './pages/marketing/Campaigns.jsx';
import ChannelPage from './pages/marketing/ChannelPage.jsx';
import Audiences from './pages/marketing/Audiences.jsx';
import MktContacts from './pages/marketing/Contacts.jsx';
import Templates from './pages/marketing/Templates.jsx';
import Journeys from './pages/marketing/Journeys.jsx';
import AIStudio from './pages/marketing/AIStudio.jsx';

import TrackDashboard from './pages/urltracking/Dashboard.jsx';
import TrackCampaigns from './pages/urltracking/Campaigns.jsx';
import TrackLinks from './pages/urltracking/Links.jsx';
import CreateLink from './pages/urltracking/CreateLink.jsx';
import TrackAnalytics from './pages/urltracking/Analytics.jsx';

/** Hold the shell back until the stores have loaded, so nothing flashes. */
function Workspace() {
  const { ready, S, menus } = useApp();

  if (!ready) return <BootScreen />;

  const home = homeFor(menus, S);

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<Navigate to={home} replace />} />

        {/*
          * The clients module lands on the report: the plans, the codes,
          * who has paid and who is using the product. It replaced a page of
          * previews — a queue, a digest and the top of the client table —
          * each of which is a whole screen of its own elsewhere, so the
          * landing page was three half-answers instead of one.
          */}
        <Route path="/dashboard" element={<ClientReport />} />
        <Route path="/clients" element={<ClientList view="clients" />} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/trials" element={<ClientList view="trials" />} />
        <Route path="/payments" element={<ClientList view="payments" />} />
        <Route path="/renewals" element={<ClientList view="renewals" />} />
        <Route path="/demo-bookings" element={<DemoBookings />} />
        <Route path="/demo-reports" element={<DemoReports />} />

        <Route path="/sales" element={<SalesOverview />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/leads/:id" element={<LeadDetail />} />
        <Route path="/deals" element={<Deals />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/activities" element={<Activities />} />
        <Route path="/follow-ups" element={<FollowUps />} />

        <Route path="/marketing" element={<Campaigns />} />
        <Route path="/marketing/email" element={<ChannelPage channel="email" />} />
        <Route path="/marketing/whatsapp" element={<ChannelPage channel="whatsapp" />} />
        <Route path="/marketing/sms" element={<ChannelPage channel="sms" />} />
        <Route path="/marketing/push" element={<ChannelPage channel="push" />} />
        <Route path="/marketing/journeys" element={<Journeys />} />
        <Route path="/marketing/studio" element={<AIStudio />} />
        <Route path="/marketing/contacts" element={<MktContacts />} />
        <Route path="/marketing/audiences" element={<Audiences />} />
        <Route path="/marketing/templates" element={<Templates />} />

        <Route path="/url-tracking" element={<TrackDashboard />} />
        <Route path="/url-tracking/campaigns" element={<TrackCampaigns />} />
        <Route path="/url-tracking/links" element={<TrackLinks />} />
        <Route path="/url-tracking/create" element={<CreateLink />} />
        <Route path="/url-tracking/analytics" element={<TrackAnalytics />} />

        <Route path="/needs-action" element={<NeedsAction />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/outbox" element={<Outbox />} />
        <Route path="/copilot" element={<Copilot />} />
        <Route path="/insights" element={<Insights />} />

        <Route path="/sales-reports" element={<SalesReports />} />

        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/:pane" element={<Settings />} />

        <Route path="*" element={<Navigate to={home} replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  /*
   * Signed out, every path redirects to /login, so the sign-in screen has a
   * real URL instead of being a gate that silently replaces the workspace.
   * Signing in falls through to the catch-all inside <Workspace>, which sends
   * the user on to their start view — the dashboard unless they changed it.
   */
  const [signedIn, setSignedIn] = useState(() => hasSession() || offlineAllowed());

  /* Fired by the API client when the session ends: a failed refresh, the
     8-hour ceiling running out, or Sign out in the workspace menu. */
  useEffect(() => {
    const onSignedOut = () => setSignedIn(false);
    window.addEventListener('rh:signed-out', onSignedOut);
    return () => window.removeEventListener('rh:signed-out', onSignedOut);
  }, []);

  return (
    <BrowserRouter>
      {signedIn ? (
        <AppProvider>
          <Workspace />
        </AppProvider>
      ) : (
        <Routes>
          <Route path="/login" element={<Login onSignedIn={() => setSignedIn(true)} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </BrowserRouter>
  );
}
